import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface UseFavoriteCountProps {
  artistId: string;
  artistType: 'mock' | 'artist' | 'shop';
}

export function useFavoriteCount({ artistId, artistType }: UseFavoriteCountProps) {
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        setLoading(true);
        
        if (artistType === 'shop') {
          // 店舗のお気に入り数を取得
          const { count: shopCount, error } = await supabase
            .from('favorite_shops')
            .select('*', { count: 'exact', head: true })
            .eq('shop_id', Number(artistId));

          if (error) throw error;
          setCount(shopCount || 0);
        } else {
          // アーティスト（mock/artist）のお気に入り数を取得
          const { count: artistCount, error } = await supabase
            .from('favorite_artists')
            .select('*', { count: 'exact', head: true })
            .eq('artist_id', artistId)
            .eq('artist_type', artistType);

          if (error) throw error;
          setCount(artistCount || 0);
        }
      } catch (error) {
        console.error('Failed to fetch favorite count:', error);
        setCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCount();

    // リアルタイム更新のためのサブスクリプション
    const tableName = artistType === 'shop' ? 'favorite_shops' : 'favorite_artists';
    const channel = supabase
      .channel(`favorite-count-${artistType}-${artistId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: tableName,
          filter: artistType === 'shop' 
            ? `shop_id=eq.${artistId}` 
            : `artist_id=eq.${artistId}`,
        },
        () => {
          // お気に入りの変更があったら再取得
          fetchCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [artistId, artistType]);

  return { count, loading };
}
